import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Search, Trash2, Download, Plus, X } from 'lucide-react';
import api from '../../services/api';

function formatPhone(phone) {
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  return phone;
}

export default function Blacklist() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newNome, setNewNome] = useState('');
  const [newMotivo, setNewMotivo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['blacklist', page, search],
    queryFn: () => api.get(`/blacklist?page=${page}&limit=50&search=${search}`).then(r => r.data),
    keepPreviousData: true,
  });

  const handleAdd = async () => {
    if (!newPhone.trim()) return;
    try {
      await api.post('/blacklist', { telefone: newPhone, nome: newNome, motivo: newMotivo || 'manual' });
      qc.invalidateQueries(['blacklist']);
      setShowAdd(false);
      setNewPhone(''); setNewNome(''); setNewMotivo('');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao adicionar');
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remover este número da blacklist?')) return;
    await api.delete(`/blacklist/${id}`);
    qc.invalidateQueries(['blacklist']);
  };

  const handleExport = () => {
    window.open(`${process.env.REACT_APP_API_URL || ''}/blacklist/export`, '_blank');
  };

  const items = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Ban className="text-red-500" size={24} />
          <h1 className="text-2xl font-bold text-gray-800">Blacklist</h1>
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{total} números</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100">
            <Download size={16} /> Exportar Excel
          </button>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            <Plus size={16} /> Adicionar
          </button>
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por telefone ou nome..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {showAdd && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800">Adicionar à Blacklist</span>
            <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input placeholder="Telefone *" value={newPhone} onChange={e => setNewPhone(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input placeholder="Nome (opcional)" value={newNome} onChange={e => setNewNome(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <select value={newMotivo} onChange={e => setNewMotivo(e.target.value)} className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option value="">Motivo...</option>
                <option value="nao_quer_contato">Não quer contato</option>
                <option value="numero_errado">Número errado</option>
                <option value="nao_conhece">Não conhece a pessoa</option>
                <option value="manual">Outro</option>
              </select>
              <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Salvar</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Telefone</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Motivo</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Origem</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Data</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">Nenhum número na blacklist</td></tr>
            ) : items.map(item => (
              <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono">{formatPhone(item.telefone)}</td>
                <td className="px-4 py-3">{item.nome || '—'}</td>
                <td className="px-4 py-3">
                  <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">
                    {item.motivo === 'nao_quer_contato' ? 'Não quer contato' :
                     item.motivo === 'numero_errado' ? 'Número errado' :
                     item.motivo === 'nao_conhece' ? 'Não conhece' : item.motivo || '—'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{item.origem || '—'}</td>
                <td className="px-4 py-3 text-gray-500">{item.criado_em ? new Date(item.criado_em).toLocaleDateString('pt-BR') : '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => handleRemove(item.id)} className="flex items-center gap-1 ml-auto text-xs font-medium text-gray-400 hover:text-red-600 transition-colors">
                    <Trash2 size={14} /> Remover
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">Anterior</button>
          <span className="text-sm text-gray-500">Página {page} de {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">Próxima</button>
        </div>
      )}
    </div>
  );
}
